/**
 * Activation-badge data hook (ADR ACTIVATION-BADGE §Frontend). Gated on customer:read — every role that
 * opens Customer-360 sees the badge (ADR decision 6), Viewer included.
 *
 * WHY THIS HOOK OVERRIDES THE APP DEFAULTS. App.tsx sets staleTime 60 s and refetchOnWindowFocus false
 * for the whole app. The badge's whole point is to move within seconds of the customer signing in, with
 * no "check now" button — freshness must come from the pipeline, not from a human clicking. So:
 *   - staleTime 15 s + refetchInterval 20 s: an open Customer-360 follows the 30 s hot-tier poll.
 *   - refetchIntervalInBackground false: a tab nobody is looking at does not keep polling.
 *   - refetchOnWindowFocus TRUE: the CSM who flips back to the portal after the customer's call sees the
 *     current state immediately instead of waiting out the interval.
 * The server answers `Cache-Control: no-store`, so nothing between here and the API can freeze it.
 */

import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import { useRbac } from "@/context/RbacContext";

export const activationKeys = {
  account: (id: string) => ["activation", id] as const,
};

/** The badge's polling contract — exported so the test pins exactly what is handed to TanStack. */
export const ACTIVATION_QUERY_OPTIONS = {
  staleTime: 15_000,
  refetchInterval: 20_000,
  refetchIntervalInBackground: false,
  refetchOnWindowFocus: true,
} as const;

export function useCustomerActivation(id: string) {
  const { hasPermission } = useRbac();
  return useQuery({
    queryKey: activationKeys.account(id),
    queryFn: ({ signal }) => api.customerActivation(id, signal),
    enabled: hasPermission("customer:read") && id.length > 0,
    ...ACTIVATION_QUERY_OPTIONS,
  });
}
